import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Check, Quote } from "lucide-react";
import { api } from "@/utils/api-client";
import {
  TRUSTED_BY, STATS, TESTIMONIALS, WORKFLOWS,
  DIFFERENTIATORS, IMPACT_METRICS, WHY,
} from "../../_data/site";

/* ─────────────────────────────  shared  ───────────────────────────── */

export function SectionHead({
  eyebrow, title, sub, align = "center",
}: { eyebrow?: string; title: string; sub?: string; align?: "left" | "center" }) {
  return (
    <div className={`mx-auto max-w-2xl ${align === "center" ? "text-center" : ""}`}>
      {eyebrow && (
        <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">{eyebrow}</div>
      )}
      <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
        {title}
      </h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-muted-foreground">{sub}</p>}
    </div>
  );
}

/* ─────────────────────────────  trusted by  ───────────────────────────── */

export function TrustedBy() {
  const [items, setItems] = useState<string[]>(TRUSTED_BY);

  useEffect(() => {
    // Admin-editable marquee items (CMS → Landing Page Stats → "Trusted by").
    api.silent
      .get<{ trustedBy?: string[] }>("/api/public/site-stats")
      .then((data) => {
        if (Array.isArray(data?.trustedBy) && data.trustedBy.length > 0) setItems(data.trustedBy);
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  // Duplicate the list so the -50% keyframe produces a seamless, continuous loop.
  const loop = [...items, ...items];

  return (
    <section className="border-y border-border bg-surface/40 py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-center text-xs uppercase tracking-[0.22em] text-muted-foreground">
          For owner-run self-drive rentals in India
        </p>
        <div className="relative mt-6 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
          <div className="animate-marquee flex w-max gap-12 whitespace-nowrap text-base font-medium text-muted-foreground/80">
            {loop.map((n, i) => (
              <span key={i} className="font-display tracking-tight">{n}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  product pillars  ───────────────────────────── */

export function ProductPillars() {
  const pillars = [
    {
      eyebrow: "Bookings & pickup",
      title: "From booking to a recorded pickup.",
      body: "Schedule the rental, check vehicle availability, set the rent and deposit, and select or add the customer. Before pickup, complete verified KYC, the condition checklist and any required deposit collection.",
      bullets: [
        "Availability checks and turnaround buffers",
        "Pre-filled, on-screen rental agreement",
        "Photo-backed pickup, every time",
      ],
      visual: <BookingVisual />,
    },
    {
      eyebrow: "Live trip lifecycle",
      title: "Know when a trip is due back.",
      body: "Active bookings show elapsed time, expected return and on-time, grace or overdue status. Extensions check for booking conflicts. Record the return first, then generate the bill with late charges, extra kilometres and added line items.",
      bullets: [
        "Color-coded on-time / grace / overdue",
        "Auto-billed overtime and extra-KM",
        "Record cash, UPI, card or bank transfers",
      ],
      visual: <TripVisual />,
      flip: true,
    },
    {
      eyebrow: "Deposits & compliance",
      title: "Every rupee, every document, every action — traceable.",
      body: "Keep refundable deposits separate from income. Deduct charges or record a refund at settlement. Expired insurance or PUC blocks new bookings, and timestamped activity records preserve the history.",
      bullets: [
        "Deposit lifecycle: HELD → REFUNDED",
        "Expired insurance or PUC blocks new bookings",
        "Immutable audit log for ops & finance",
      ],
      visual: <ComplianceVisual />,
    },
  ];

  return (
    <section className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="The platform"
          title="The rental loop, in one app."
          sub="Bookings, counter KYC, deposits, pickup photos, live trips, return settlement and invoices — with a record at each step."
        />

        <div className="mt-14 space-y-16 sm:mt-20 sm:space-y-28">
          {pillars.map((p) => (
            <div
              key={p.title}
              className={`grid items-center gap-8 lg:grid-cols-12 lg:gap-10 ${p.flip ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-5">
                <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">{p.eyebrow}</div>
                <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">{p.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{p.body}</p>
                <ul className="mt-6 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-7">
                <div className="relative">
                  <div className="absolute -inset-6 -z-10 bg-gradient-mesh opacity-50 blur-3xl" />
                  {p.visual}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────  pillar visuals — single, composed product previews  ───────────── */

function MockFrame({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-elegant">
      <div className="flex items-center justify-between border-b border-border bg-background/60 px-4 py-2">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-destructive/60" />
          <span className="h-2 w-2 rounded-full bg-yellow-500/60" />
          <span className="h-2 w-2 rounded-full bg-primary/60" />
        </div>
        <div className="text-[10px] text-muted-foreground">{title}</div>
        <div className="w-8" />
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function BookingVisual() {
  const steps = ["Schedule", "Vehicle", "Pricing", "Customer", "Confirm"];
  return (
    <MockFrame title="Illustrative preview · new booking">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-medium ${i <= 2 ? "bg-gradient-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
            <span className={`text-[11px] ${i <= 2 ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < steps.length - 1 && <span className={`mx-1 hidden h-px w-4 sm:block ${i < 2 ? "bg-primary" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicle</div>
          <div className="mt-2 rounded-lg border border-primary/40 bg-primary/5 p-3">
            <div className="text-sm font-medium">Honda Activa 6G</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">MH12·QR04 · Available · Next booking 6h away</div>
          </div>
          <div className="mt-2 rounded-lg border border-border p-3 opacity-60">
            <div className="text-sm">Suzuki Access 125</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">MH14·CV82 · Available</div>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pricing</div>
          <dl className="mt-2 space-y-1.5 text-[12px]">
            {[["Rent (24h)", "₹450"], ["Deposit", "₹1,000"], ["KM limit", "120 km"], ["Extra KM", "₹6/km"]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-border/60 py-1">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-medium">{v}</dd>
              </div>
            ))}
            <div className="flex justify-between pt-1.5 font-display text-sm">
              <dt>Total at pickup</dt>
              <dd className="gradient-text font-semibold">₹1,450</dd>
            </div>
          </dl>
        </div>
      </div>
    </MockFrame>
  );
}

function TripVisual() {
  return (
    <MockFrame title="Illustrative preview · active trip">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Trip timer</div>
          <div className="mt-1 font-display text-3xl font-semibold text-destructive">02 : 42</div>
          <div className="mt-1 text-[11px] text-destructive">Overdue · late charge estimate</div>
        </div>
        <div className="rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-1 text-[10px] font-medium text-destructive">
          Overdue
        </div>
      </div>

      <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full w-[112%] bg-gradient-to-r from-primary via-yellow-500 to-destructive" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-[11px]">
        <div className="rounded-lg border border-border p-2.5">
          <div className="text-muted-foreground">Customer</div>
          <div className="mt-0.5 font-medium">Niharika S.</div>
        </div>
        <div className="rounded-lg border border-border p-2.5">
          <div className="text-muted-foreground">Vehicle</div>
          <div className="mt-0.5 font-medium">Swift Dzire</div>
        </div>
        <div className="rounded-lg border border-border p-2.5">
          <div className="text-muted-foreground">Expected</div>
          <div className="mt-0.5 font-medium">14:30</div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-background/40 p-3 text-[11px]">
        <div className="mb-2 text-muted-foreground">Live ops feed</div>
        {[
          ["09:14", "Pickup checklist completed", "primary"],
          ["13:10", "Grace window started (15m)", "yellow"],
          ["13:25", "Overdue · late charge estimate", "destructive"],
        ].map(([t, m, c]) => (
          <div key={String(t)} className="flex items-center justify-between border-t border-border/60 py-1.5 first:border-t-0 first:pt-0">
            <span>{m}</span>
            <span className={`font-mono text-[10px] ${c === "primary" ? "text-primary" : c === "yellow" ? "text-yellow-500" : "text-destructive"}`}>{t}</span>
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

function ComplianceVisual() {
  return (
    <MockFrame title="Illustrative preview · vehicle documents">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-semibold">Honda Activa 6G</div>
          <div className="text-[11px] text-muted-foreground">MH12·QR04 · Vehicle record</div>
        </div>
        <div className="rounded-full bg-destructive/15 px-2.5 py-1 text-[10px] font-medium text-destructive">
          Blocked · PUC expired
        </div>
      </div>

      <div className="mt-4 space-y-2 text-[12px]">
        {[
          { l: "Registration (RC)", v: "Valid till Aug 2031", c: "primary" },
          { l: "Insurance", v: "Renews in 27 days", c: "yellow" },
          { l: "PUC", v: "Expired 4 days ago", c: "destructive" },
        ].map((d) => (
          <div key={d.l} className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2">
            <span className="text-muted-foreground">{d.l}</span>
            <span className={`font-medium ${d.c === "primary" ? "text-primary" : d.c === "yellow" ? "text-yellow-500" : "text-destructive"}`}>{d.v}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-border bg-background/40 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deposit on booking B-1041</div>
        <div className="mt-2 flex items-center justify-between text-[11px]">
          <div>
            <div className="font-medium">₹1,000 held</div>
            <div className="text-muted-foreground">→ ₹420 deducted → ₹580 refunded</div>
          </div>
          <div className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">Settled</div>
        </div>
      </div>
    </MockFrame>
  );
}

/* ─────────────────────────────  workflows  ───────────────────────────── */

export function WorkflowsSection() {
  const [active, setActive] = useState(0);
  const w = WORKFLOWS[active];

  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="End-to-end workflows"
          title="Every lifecycle, captured in one structured flow."
          sub="No more switching between WhatsApp, spreadsheets and the printer. Pick a workflow and see how it runs inside RentOS."
        />

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <nav className="min-w-0 lg:col-span-4">
            {/* On mobile the tabs form a 2-col grid (no horizontal overflow);
                on lg+ they stack vertically. */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-1">
              {WORKFLOWS.map((wf, i) => (
                <button
                  key={wf.key}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`group relative flex min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm transition-all sm:px-4 sm:py-3 ${
                    i === active
                      ? "border-primary/50 bg-primary/10 text-foreground shadow-glow"
                      : "border-border text-muted-foreground hover:border-border hover:bg-surface/60 hover:text-foreground"
                  }`}
                >
                  <wf.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="min-w-0 truncate font-medium">{wf.name}</span>
                </button>
              ))}
            </div>
          </nav>

          <div className="min-w-0 lg:col-span-8">
            <motion.div
              key={w.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border border-border bg-surface/60 p-5 shadow-elegant backdrop-blur sm:p-7"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-primary text-primary-foreground">
                  <w.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-semibold">{w.name}</h3>
                  <p className="text-sm text-muted-foreground">{w.summary}</p>
                </div>
              </div>

              <ol className="mt-7 space-y-4">
                {w.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full border border-primary/40 bg-primary/10 text-[11px] font-semibold text-primary">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <p className="pt-0.5 text-sm leading-relaxed text-foreground/90">{s}</p>
                  </li>
                ))}
              </ol>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  before / after  ───────────────────────────── */

export function ImpactSection() {
  return (
    <section className="relative border-y border-border bg-surface/30 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="What changes"
          title="The day you stop running rentals on paper."
          sub="How the recorded workflow differs from paper registers and scattered messages."
        />

        <div className="mt-14 overflow-hidden rounded-2xl border border-border">
          {/* header row — desktop only */}
          <div className="hidden grid-cols-12 border-b border-border bg-background/60 px-6 py-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:grid">
            <div className="col-span-4">Workflow</div>
            <div className="col-span-4">Before RentOS</div>
            <div className="col-span-4">With RentOS</div>
          </div>
          {IMPACT_METRICS.map((m, i) => (
            <div
              key={m.label}
              className={`gap-x-2 gap-y-1.5 px-5 py-4 sm:grid sm:grid-cols-12 sm:items-center sm:px-6 sm:py-5 ${i % 2 ? "bg-background/30" : ""}`}
            >
              <div className="font-display text-base font-medium sm:col-span-4">{m.label}</div>
              <div className="mt-1 flex items-center gap-2 sm:col-span-4 sm:mt-0">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 sm:hidden">Before:</span>
                <span className="text-sm text-muted-foreground line-through decoration-destructive/70">{m.before}</span>
              </div>
              <div className="mt-1 flex items-center gap-2 sm:col-span-4 sm:mt-0">
                <span className="text-[10px] uppercase tracking-wider text-primary/70 sm:hidden">Now:</span>
                <span className="text-sm font-medium text-primary">{m.after}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-x-10 gap-y-5 sm:grid-cols-2">
          {DIFFERENTIATORS.map((d) => (
            <div key={d.before} className="flex items-start gap-4 border-t border-border pt-5">
              <div className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground line-through decoration-destructive/60">{d.before}</p>
                <p className="text-sm font-medium text-foreground">{d.after}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  why  ───────────────────────────── */

export function Why() {
  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="Why operators move to RentOS"
          title="Built around the owner’s rental day."
          sub="For owner-run businesses managing roughly 5–50 bikes, scooters or cars — from counter KYC to return settlement."
        />

        <div className="mx-auto mt-14 grid max-w-6xl gap-y-10 gap-x-12 sm:grid-cols-2">
          {WHY.map((w) => (
            <div key={w.title} className="flex items-start gap-5 border-t border-border pt-6">
              <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-border bg-surface text-primary">
                <w.icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">{w.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  stats  ───────────────────────────── */

type SiteStat = { label: string; value: number; suffix: string; display?: string };

function useCount(target: number, duration = 1600, run = false) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return;
    let start: number | null = null;
    let raf = 0;
    const tick = (t: number) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / duration);
      setV(Math.floor(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, run]);
  return v;
}

function StatItem({ s, run }: { s: SiteStat; run: boolean }) {
  const v = useCount(s.value, 1500, run);
  // If the API gave a pre-formatted display string, show it directly.
  const display = s.display ?? (s.value >= 1000 ? v.toLocaleString() : String(v));
  return (
    <div className="text-center sm:text-left">
      <div className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
        <span className="gradient-text">{display}</span>
        <span className="text-foreground">{s.suffix}</span>
      </div>
      <div className="mt-2 text-sm text-muted-foreground">{s.label}</div>
    </div>
  );
}

export function StatsBand() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  const [stats, setStats] = useState<SiteStat[]>(STATS);

  useEffect(() => {
    // Pull live site stats; fall back silently to the static product facts.
    api.silent
      .get<Record<string, string | number>>("/api/public/site-stats")
      .then((data) => {
        if (!data) return;
        const mapped: SiteStat[] = [
          { key: "rentalBusinesses", label: "Rental businesses" },
          { key: "bookingsProcessed", label: "Bookings processed" },
          { key: "vehiclesTracked", label: "Vehicles tracked" },
          { key: "uptimeSla", label: "Uptime SLA" },
        ]
          .filter((m) => data[m.key] != null)
          .map((m) => ({ label: m.label, value: 0, suffix: "", display: String(data[m.key]) }));
        if (mapped.length === 4) setStats(mapped);
      })
      .catch(() => { /* keep static STATS */ });
  }, []);

  return (
    <section className="relative py-20 sm:py-24" ref={ref}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 gap-y-10 sm:grid-cols-4">
          {stats.map((s) => (
            <StatItem key={s.label} s={s} run={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  testimonial / principles  ───────────────────────────── */

type Spotlight = { name: string; role: string; quote: string };

export function TestimonialSpotlight() {
  const [items, setItems] = useState<Spotlight[]>(TESTIMONIALS);
  const [i, setI] = useState(0);

  useEffect(() => {
    // Prefer real testimonials from the CMS; fall back to product principles.
    api.silent
      .get<Array<{ quote: string; author: string; role: string }>>("/api/public/testimonials")
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setItems(data.map((t) => ({ name: t.author, role: t.role, quote: t.quote })));
        }
      })
      .catch(() => { /* keep static principles */ });
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [items.length]);

  const t = useMemo(() => items[Math.min(i, items.length - 1)], [items, i]);
  if (!t) return null;

  return (
    <section className="relative border-y border-border bg-surface/30 py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <Quote className="mx-auto h-8 w-8 text-primary/70" />
        <motion.blockquote
          key={t.name}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-6 font-display text-2xl font-medium leading-snug tracking-tight sm:text-3xl md:text-4xl"
        >
          &ldquo;{t.quote}&rdquo;
        </motion.blockquote>
        <div className="mt-8 text-sm">
          <div className="font-medium">{t.name}</div>
          <div className="text-muted-foreground">{t.role}</div>
        </div>

        {items.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            {items.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setI(idx)}
                aria-label={`Show item ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-8 bg-primary" : "w-1.5 bg-border"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────  testimonials grid (real quotes only)  ───────────────────────────── */

type RealTestimonial = { id: string; quote: string; author: string; role: string; avatar?: string };

function initials(name: string): string {
  return name.split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}

export function Testimonials() {
  const [items, setItems] = useState<RealTestimonial[]>([]);

  useEffect(() => {
    api.silent
      .get<Array<Record<string, unknown>>>("/api/public/testimonials")
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setItems(data.map((t) => ({
            id: String(t.id ?? t._id),
            quote: String(t.quote ?? ""),
            author: String(t.author ?? ""),
            role: String(t.role ?? ""),
            avatar: t.avatar ? String(t.avatar) : undefined,
          })));
        }
      })
      .catch(() => { /* no section if none */ });
  }, []);

  // Only render when there are real, admin-published testimonials.
  if (items.length === 0) return null;

  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="From rental owners"
          title="Operators who run on RentOS."
          sub="Real feedback from the businesses using RentOS day to day."
        />

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <figure key={t.id} className="flex h-full flex-col rounded-2xl border border-border bg-surface/40 p-6">
              <Quote className="h-6 w-6 text-primary/60" />
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground/90">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                {t.avatar && t.avatar.startsWith("http") ? (
                  <img src={t.avatar} alt={t.author} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-primary text-sm font-semibold text-primary-foreground">
                    {t.avatar && !t.avatar.startsWith("http") ? t.avatar : initials(t.author)}
                  </div>
                )}
                <div>
                  <div className="text-sm font-semibold">{t.author}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────  closing CTA  ───────────────────────────── */

export function ClosingCTA() {
  return (
    <section className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-surface/60 p-10 text-center shadow-elegant sm:p-16">
          <div className="absolute inset-0 -z-10 bg-gradient-mesh opacity-60" />
          <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl md:text-5xl">
            Run your rental business on{" "}
            <span className="gradient-text">one platform.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Use the free tier to get started. Choose paid modules and limits as your fleet grows.
            Run the same Business app on the web, Android and iOS.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-glow transition-transform hover:-translate-y-0.5"
            >
              Book a demo <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-background/50 px-6 py-3 text-sm font-medium backdrop-blur transition-colors hover:bg-surface"
            >
              See pricing
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
